import { jsx } from 'panel';
import { QueryBlockEl } from 'common/widgets/query-block';
import { serializeAttributes } from 'common/util';
import { SelectEl } from 'common/components/select';
import { EntryPart, FiltersOperator, SelectorTypeDefinitions } from 'common/widgets/query-entry-section/types';
import { PropertyType, ResourceType } from 'common/widgets/property-filter-menu/models/types';
import { isEnhancedListFilter } from 'common/widgets/property-filter-menu/utils';
import { preventTracking } from 'common/util/dom';
import { isFirstTimeEventFilter } from 'common/widgets/property-filter-menu/models/utils';
import { BaseEntryRowEl } from '../base/template';
import { FilterNumericValue } from './types/number';
import { FilterStringValue } from './types/string';
import { FilterListValue } from './types/list';
import { FilterObjectValue } from './types/object';
import { FilterDateValue } from './types/date';
const FilterValueTemplate = {
  [PropertyType.Number]: FilterNumericValue,
  [PropertyType.String]: FilterStringValue,
  [PropertyType.Object]: FilterObjectValue,
  [PropertyType.List]: FilterListValue,
  [PropertyType.Datetime]: FilterDateValue,
  // the following property type filters do not render a value definition
  [PropertyType.Boolean]: null,
  [PropertyType.Dimension]: null,
  [PropertyType.NonProperty]: null,
  [PropertyType.Unknown]: null
}; // defines which filter types do not render an operator selector
// this is a temporal check until we can bring all of the new filter
// UI to all filter types

const NO_OPERATOR_TYPES = new Set([PropertyType.Object, PropertyType.NonProperty]);
export function FilterEntryRowEl(_ref) {
  let {
    section,
    entry,
    index
  } = _ref;
  const {
    activeEntry,
    entryPart
  } = section.state;
  const hasProperty = this.filter.hasSelectedProperty();
  const queryBlockConfig = section.getQueryBlockConfig(SelectorTypeDefinitions.Filter);
  const isCohortFilter = this.filter.resourceType === ResourceType.Cohort;

  const selectorRenderer = () => jsx(null, null, this.entry.filtersOperator && jsx("div", {
    sel: '.entry-selector-prefix'
  }, jsx(RowFiltersPrefix, {
    filtersOperator: this.entry.filtersOperator,
    filterIndex: this.index,
    filter: this.filter,
    // prevent changing filtes operator when staging another entry
    forceDisabled: section.helpers.isAddingDisabled(),
    handleChange: ev => this.handleEntryFilterOperatorChange(ev.detail.value)
  })), jsx(QueryBlockEl, {
    key: 'entry-selector',
    class: {
      'is-placeholder': !hasProperty
    },
    attrs: serializeAttributes(Object.assign(Object.assign({}, queryBlockConfig), {
      elref: "entrySelector",
      'allow-typecast': true,
      'disable-value-selection': true,
      open: !hasProperty && activeEntry === index && entryPart === EntryPart.Value,
      'data-group-id': section.attr("data-group-id"),
      'single-data-group': section.attr("data-group-id"),
      'inline-cohorts-override': section.attr("inline-cohorts-override"),
      'project-has-cohorts': section.attr("project-has-cohorts"),
      'project-id': section.attr("project-id"),
      selected: hasProperty ? this.filter.getAttributes() : null,
      'show-special-dataset-property': section.attr("show-special-dataset-property"),
      'hide-special-date-property-unit': section.attr("hide-special-date-property-unit"),
      theme: "select",
      'track-name': section.attr("track-name"),
      'selector-type': "button",
      'user-id': section.attr("user-id"),
      disabled: section.attr("disabled"),
      'workspace-id': section.attr("workspace-id")
    })),
    on: {
      close: () => section.helpers.setActiveEntry("close", index, EntryPart.Value),
      open: () => section.helpers.setActiveEntry("open", index, EntryPart.Value),
      remove: () => section.helpers.onEventActionClick(section.helpers.Action.Remove, index),
      select: ev => this.handlePropertyItemSelected(ev),
      track: preventTracking
    }
  }), isEnhancedListFilter(this.filter) && jsx(SelectEl, {
    attrs: {
      elref: "listFilterQuantifier",
      'menu-width': "200",
      theme: "select",
      tooltip: this.getListQuantifierTooltip(this.filter.listQuantifier)
    },
    props: {
      sections: this.getListQuantifierSections()
    },
    on: {
      change: (_ref2) => {
        let {
          detail
        } = _ref2;
        return this.setFilterAttribute("listQuantifier", detail.item.value);
      }
    }
  }), this.filter.hasSelectedProperty() && !isCohortFilter && !NO_OPERATOR_TYPES.has(this.filter.propertyType) && jsx(SelectEl, {
    attrs: {
      elref: "filterOperator",
      'menu-width': "200",
      'menu-height': "400",
      'selected-option': this.getFilterOperatorSymbol(),
      theme: "select",
      tooltip: this.getFilterOperatorTooltip()
    },
    props: {
      sections: this.getFilterOperatorsSections()
    },
    on: {
      change: ev => this.handleFilterOperatorChange(ev)
    }
  }), !isCohortFilter && FilterValueTemplate[this.filter.propertyType] && FilterValueTemplate[this.filter.propertyType].call(this));

  return jsx(BaseEntryRowEl, {
    index: index,
    el: section,
    entry: entry,
    rowRenderer: {
      prefixRenderer: null,
      selectorRenderer,
      suffixRenderer: null
    },
    isPlaceholder: this.isPlaceholder()
  });
}
export function RowFiltersPrefix(_ref3) {
  let {
    filtersOperator,
    filter,
    filterIndex,
    handleChange,
    forceDisabled = false
  } = _ref3;
  let canSwitch = false;
  let label = filtersOperator.toString(); // behavior filter as first filter

  if (filter && isFirstTimeEventFilter(filter)) {
    label = "for the"; // first non-behavior filter
  } else if (filterIndex === 0) {
    label = "where";
  } else {
    canSwitch = true;
  } // and/or switch


  return jsx(SelectEl, {
    class: {
      'is-disabled': forceDisabled ? true : !canSwitch
    },
    attrs: {
      elref: "filtersOperatorSelect",
      'menu-width': "60",
      options: [FiltersOperator.And, FiltersOperator.Or],
      'selected-option': label,
      theme: "tertiary"
    },
    on: {
      change: handleChange
    }
  });
}